// src/components/student/diagnostics/diagnosticLiveProgress.js
//
// Lightweight, queryable "live progress" tracking for diagnostics.
//
// The student test-takers (SAT / PSAT / ACT) already autosave their full
// resume state to per-user subcollections (diagnostic_results/{uid}/state/...
// and psat_diagnostic_results/{uid}/state/...). Those docs are great for
// resuming a test, but they are awkward to surface on an admin dashboard:
// they live under per-student paths and (for PSAT) are locked to the owner.
//
// This module writes a small, flat summary document to a single top-level
// collection (`diagnostic_live_progress`), keyed by `${studentId}_${testId}`,
// so the Admin HomeScreen can read every active/just-finished diagnostic with
// one simple query. It is intentionally additive — it never replaces the
// existing autosave/resume logic.
//
// Firestore rules required (see firestore.rules): students may write a doc
// whose `studentId` is their own uid; admins may read everything.

import { db } from "../../../firebaseConfig";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";

export const DIAGNOSTIC_LIVE_PROGRESS_COL = "diagnostic_live_progress";

// Ordered "stages" per test type. Each stage carries a friendly label and its
// nominal duration (seconds) so we can estimate the total time remaining. The
// `match` keys map the test-taker's internal module/section keys onto a stage.
const STAGE_PLANS = {
  SAT: [
    { key: "rw1", label: "Reading & Writing · Module 1", secs: 32 * 60, match: ["RW_Routing"] },
    { key: "rw2", label: "Reading & Writing · Module 2", secs: 32 * 60, match: ["RW_Module_A", "RW_Module_B"] },
    { key: "break", label: "Break", secs: 10 * 60, match: ["BREAK"] },
    { key: "m1", label: "Math · Module 1", secs: 35 * 60, match: ["Math_Routing"] },
    { key: "m2", label: "Math · Module 2", secs: 35 * 60, match: ["Math_Module_A", "Math_Module_B"] },
  ],
  PSAT: [
    { key: "rw1", label: "Reading & Writing · Module 1", secs: 32 * 60, match: ["English_Routing_Module"] },
    { key: "rw2", label: "Reading & Writing · Module 2", secs: 32 * 60, match: ["English_Module_A", "English_Module_B"] },
    { key: "break", label: "Break", secs: 10 * 60, match: ["BREAK"] },
    { key: "m1", label: "Math · Module 1", secs: 35 * 60, match: ["Math_Routing_Module"] },
    { key: "m2", label: "Math · Module 2", secs: 35 * 60, match: ["Math_Module_A", "Math_Module_B"] },
  ],
  ACT: [
    { key: "english", label: "English", secs: 35 * 60, match: ["English"] },
    { key: "math", label: "Math", secs: 50 * 60, match: ["Math"] },
    { key: "break", label: "Break", secs: 10 * 60, match: ["BREAK"] },
    { key: "reading", label: "Reading", secs: 40 * 60, match: ["Reading"] },
    { key: "science", label: "Science", secs: 40 * 60, match: ["Science"] },
  ],
};

function normalizeType(testType) {
  const t = String(testType || "").toUpperCase();
  return STAGE_PLANS[t] ? t : "SAT";
}

function findStageIndex(plan, stageKey) {
  return plan.findIndex((s) => s.match.includes(stageKey));
}

// SAT/PSAT path history entries look like { module, score }. Map them onto the
// stage plan, drop the break, and keep the raw module score for display.
function completedFromPathHistory(plan, pathHistory) {
  if (!Array.isArray(pathHistory)) return [];
  const out = [];
  pathHistory.forEach((h) => {
    const idx = findStageIndex(plan, h?.module);
    if (idx < 0) return;
    const stage = plan[idx];
    if (stage.key === "break") return;
    out.push({ key: stage.key, label: stage.label, rawScore: Number.isFinite(Number(h?.score)) ? Number(h.score) : null });
  });
  return out;
}

// ACT has no path history; everything before the current section (minus the
// break) is "completed". Per-section scores are only known at finish, so no score.
function completedFromOrder(plan, currentIndex) {
  if (currentIndex < 0) return [];
  return plan
    .slice(0, currentIndex)
    .filter((s) => s.key !== "break")
    .map((s) => ({ key: s.key, label: s.label, rawScore: null }));
}

/**
 * Upsert the live-progress summary for an in-progress diagnostic.
 * Safe to call from an autosave path — fully overwrites the summary each time.
 * Never throws (errors are swallowed) so it can't break the test experience.
 */
export async function saveDiagnosticLiveProgress({
  testType,
  testId,
  studentId,
  studentName = "",
  stageKey,
  answeredCount = 0,
  totalQuestions = 0,
  sectionTimeLeftSeconds = 0,
  pathHistory = null,
} = {}) {
  try {
    if (!studentId || !testId) return;
    const type = normalizeType(testType);
    const plan = STAGE_PLANS[type];
    const idx = findStageIndex(plan, stageKey);
    const stage = idx >= 0 ? plan[idx] : null;

    const sectionTimeLeft = Math.max(0, Math.round(Number(sectionTimeLeftSeconds) || 0));
    const remainingAfter = idx >= 0 ? plan.slice(idx + 1).reduce((sum, s) => sum + s.secs, 0) : 0;
    const estimatedTotalTimeLeft = sectionTimeLeft + remainingAfter;

    const completedSections =
      type === "ACT" ? completedFromOrder(plan, idx) : completedFromPathHistory(plan, pathHistory);

    const docId = `${studentId}_${testId}`;
    await setDoc(
      doc(db, DIAGNOSTIC_LIVE_PROGRESS_COL, docId),
      {
        studentId,
        studentName: studentName || "",
        testType: type,
        testId,
        attemptId: docId,
        status: "in_progress",
        currentStageKey: stage ? stage.key : String(stageKey || ""),
        currentSectionLabel: stage ? stage.label : String(stageKey || "—"),
        isBreak: stage ? stage.key === "break" : false,
        questionsAnswered: Math.max(0, Math.round(Number(answeredCount) || 0)),
        totalQuestions: Math.max(0, Math.round(Number(totalQuestions) || 0)),
        sectionTimeLeftSeconds: sectionTimeLeft,
        estimatedTotalTimeLeftSeconds: estimatedTotalTimeLeft,
        completedSections,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    // Non-critical: progress tracking must never interfere with the test.
    console.warn("saveDiagnosticLiveProgress failed", err);
  }
}

/**
 * Mark the live-progress summary as completed when a diagnostic finishes.
 * Stores the attemptId + a small score summary so the admin dashboard can link
 * straight to the diagnostic report.
 */
export async function markDiagnosticLiveProgressComplete({
  testType,
  testId,
  studentId,
  attemptId = null,
  scoreSummary = null,
} = {}) {
  try {
    if (!studentId || !testId) return;
    const type = normalizeType(testType);
    const docId = `${studentId}_${testId}`;
    await setDoc(
      doc(db, DIAGNOSTIC_LIVE_PROGRESS_COL, docId),
      {
        studentId,
        testType: type,
        testId,
        attemptId: attemptId || docId,
        status: "completed",
        scoreSummary: scoreSummary || null,
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn("markDiagnosticLiveProgressComplete failed", err);
  }
}
